import React, { useState, useRef, useEffect } from "react";
import {
  Spinner,
  Flex,
  Box,
  Radio,
  Label,
  Heading,
  Text,
} from "@theme-ui/components";
import ReCAPTCHA from "react-google-recaptcha";
import queryString from "query-string";
import PropType from "prop-types";
import _ from "lodash";
import { Button, InputBox } from "../atoms";
import { colors, typography } from "../../styles/theme";
import { validate, saveForm } from "../hubspot/hsAPI";
import { getButtonId } from "../../utils/common";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import styled from "@emotion/styled";
import api from "../../utils/api";
import {
  pushCTAClickEventToDataLayer,
  pushProductInterestFormEventData,
} from "../../utils/push-gtm-variable";

let recaptchaRef = React.createRef();

const Input = ({
  label,
  name,
  value,
  maxlength,
  error,
  onChange,
  labelColor,
  errorColor,
  ...props
}) => (
  <InputBox
    label={label}
    isRequired={true}
    labelColor={labelColor}
    errorColor={errorColor}
    name={name}
    value={value}
    maxlength={maxlength}
    customStyles={{
      width: "18.938rem",
      "@media(max-width: 599px)": {
        width: "17.313rem",
      },
      "@media screen and (min-width: 900px) and (max-width: 1100px)": {
        width: "15.75rem",
      },
      "@media screen and (min-width: 280px) and (max-width: 350px)": {
        px: "1rem",
      },
    }}
    error={error}
    onChange={onChange}
    {...props}
  />
);
const ErrorPage = ({ onRetry }) => {
  return (
    <Flex sx={styles.container} as="form">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <video autoPlay loop preload="auto" style={{ height: "12.5rem" }}>
            <source
              src="https://www.datocms-assets.com/40521/1726654366-exclamation.mp4"
              type="video/mp4"
            />
          </video>
        </Box>
        <Text
          as="h3"
          sx={{ ...typography.h3, textAlign: "center", mb: "1.5rem" }}
        >
          <span style={{ color: colors.primary700 }}>Oops!</span> Something went
          wrong. Please try again.
        </Text>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button variant="filled" size="large" onClick={onRetry}>
            Try Again
          </Button>
        </Box>
      </Box>
    </Flex>
  );
};

const SuccessPage = () => {
  return (
    <Flex sx={styles.container} as="form">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <video autoPlay loop preload="auto" style={{ height: "12.5rem" }}>
            <source
              src="https://www.datocms-assets.com/40521/1726655911-successform.mp4"
              type="video/mp4"
            />
          </video>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Text as="h2" sx={{ ...typography.h2, mb: "1.5rem" }}>
            <span style={{ color: colors.primary700 }}> Thank you! </span>
          </Text>
          <Text as="h3" sx={typography.h3}>
            Our team will get in touch with you soon.
          </Text>
        </Box>
      </Box>
    </Flex>
  );
};

const OTPForm = ({
  pageName,
  regionCode,
  requestOtp,
  email,
  setShowOtpForm,
  saveFormValues,
  pageNameGA,
}) => {
  const [otp, setOTP] = useState("");
  const [processing, setProcessing] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [error, setError] = useState(null);
  const btnRef = useRef(null);
  const [showResendLink, setShowResendLink] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const disableBtn = () => {
    if (btnRef.current) {
      btnRef.current.setAttribute("disabled", true);
    }
  };

  const undisableBtn = () => {
    if (btnRef.current) {
      btnRef.current.removeAttribute("disabled");
    }
  };

  useEffect(() => {
    if (otpSent) {
      const resendOtpTimerInterval = setInterval(
        () => setShowResendLink(true),
        30000
      );
      return () => {
        if (resendOtpTimerInterval) {
          clearInterval(resendOtpTimerInterval);
        }
      };
    }
  }, [otpSent]);

  const resendOtp = (event) => {
    event.preventDefault();
    requestOtp();
    setError("");
    setOTP("");
    setOtpSent(true);
    setShowResendLink(false);
  };

  const verfiyOtp = (event) => {
    event.preventDefault();
    disableBtn(btnRef);
    if (otp) {
      api
        .post("/verify_email_otp/", { email, otp })
        .then((response) => {
          const { is_valid } = response.data;
          if (is_valid) {
            setOtpVerified(true);
            saveFormValues();
            pushProductInterestFormEventData({
              form_id:
                regionCode === "IN"
                  ? `WF_PP_${pageNameGA}`
                  : `WF_PP_${pageNameGA}_ME`,
              step_id: "otp_verification_step",
              region_code: regionCode,
              form_state: "SUCCESS",
              gc_name: `${pageNameGA.toLowerCase()}_submitted_${regionCode}`,
              gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
            });
          } else {
            setError("Invalid OTP!");
            undisableBtn(btnRef);
            pushProductInterestFormEventData({
              form_id:
                regionCode === "IN"
                  ? `WF_PP_${pageNameGA}`
                  : `WF_PP_${pageNameGA}_ME`,
              step_id: "otp_verification_step",
              region_code: regionCode,
              form_state: "FAILED",
              gc_name: `${pageNameGA.toLowerCase()}_submitted_${regionCode}`,
              gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
            });
          }
        })
        .catch((error) => {
          setError(error);
          undisableBtn(btnRef);
          pushProductInterestFormEventData({
            form_id:
              regionCode === "IN"
                ? `WF_PP_${pageNameGA}`
                : `WF_PP_${pageNameGA}_ME`,
            step_id: "otp_verification_step",
            region_code: regionCode,
            form_state: "FAILED",
            gc_name: `${pageNameGA.toLowerCase()}_submitted_${regionCode}`,
            gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
          });
        });
    } else {
      setError("Please enter OTP");
      undisableBtn(btnRef);
    }
  };

  return (
    <Flex sx={styles.container} as="form">
      <Box sx={styles.otpContainer}>
        <Heading as="h5" sx={styles.otpHeading}>
          An
          <span style={{ color: colors.primaryMain }}> OTP </span>
          has been sent to your email address.
        </Heading>
        <Text as="p" sx={{ ...typography.textMd400, mb: "1.563rem" }}>
          {" "}
          Please enter the same.
        </Text>
        <Input
          type="tel"
          label="OTP"
          labelColor={colors.black}
          errorColor={colors.errorMain}
          value={otp}
          name="otp"
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              setOTP(value);
              if (error) {
                setError(null);
              }
            }
          }}
        />
        {error && <Box sx={{ color: "red" }}>{error}</Box>}
        <Box sx={{ ...typography.textMd400, mt: "0.5rem" }}>
          Didn’t receive OTP?
          <span
            style={{
              color: colors.primaryMain,
              cursor: showResendLink ? "pointer" : "default",
            }}
            onClick={resendOtp}
            disabled={!showResendLink}
          >
            {" "}
            Resend OTP{" "}
          </span>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "1.5rem",
        }}
      >
        {processing ? (
          <Spinner color="white" size={58} />
        ) : (
          <Button
            variant="filled"
            size="large"
            disabled={!otp}
            ref={btnRef}
            onClick={verfiyOtp}
          >
            Submit
          </Button>
        )}
        <Text
          as="span"
          onClick={() => setShowOtpForm(false)}
          sx={{ color: colors.primaryMain, cursor: "pointer" }}
        >
          Edit Email
        </Text>
      </Box>
    </Flex>
  );
};

const Form = ({
  formCode,
  pageCode,
  pageName,
  pageUrl,
  regionCode,
  searchParams,
  pageNameGA,
}) => {
  const utm_source = searchParams["utm_source"];
  const utm_medium = searchParams["utm_medium"];
  const utm_campaign = searchParams["utm_campaign"];

  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [isExistingCustomer, setIsExistingCustomer] = useState("Yes");

  const [message, setMessage] = useState();
  const [showOtpForm, setShowOtpForm] = useState(false);

  const [error, setError] = useState({});
  const [processing, setProcessing] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);

  const phoneNoLength = regionCode === "ME" ? 15 : 10;

  const validateForm = () => {
    let error = {};
    if (email) {
      const emailErr = validate(email);
      emailErr && (error["email"] = emailErr);
    }
    return error;
  };

  const requestOtp = (event) => {
    event?.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setError(validationErrors);
      return;
    }
    if (email) {
      api
        .post("/generate_and_send_email_otp/", { email })
        .then((response) => {
          const { sent } = response.data;
          if (!sent) {
            setMessage("Invalid Email Id");
            pushProductInterestFormEventData({
              form_id:
                regionCode === "IN"
                  ? `WF_PP_${pageNameGA}`
                  : `WF_PP_${pageNameGA}_ME`,
              step_id: "otp_request_step",
              region_code: regionCode,
              form_state: "FAILED",
              gc_name: `${pageNameGA.toLowerCase()}_confirm_${regionCode}`,
              gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
            });
          } else {
            setShowOtpForm(true);
            pushProductInterestFormEventData({
              form_id:
                regionCode === "IN"
                  ? `WF_PP_${pageNameGA}`
                  : `WF_PP_${pageNameGA}_ME`,
              step_id: "otp_request_step",
              region_code: regionCode,
              form_state: "SUCCESS",
              gc_name: `${pageNameGA.toLowerCase()}_confirm_${regionCode}`,
              gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
            });
          }
        })
        .catch((error) => {
          console.log("Otp request failed: ", error);
          setError({ email: "Please enter a valid Email Address." });
          setMessage("Please try again later");
          pushProductInterestFormEventData({
            form_id:
              regionCode === "IN"
                ? `WF_PP_${pageNameGA}`
                : `WF_PP_${pageNameGA}_ME`,
            step_id: "otp_request_step",
            region_code: regionCode,
            form_state: "FAILED",
            gc_name: `${pageNameGA.toLowerCase()}_confirm_${regionCode}`,
            gc_source: `${pageNameGA.toLowerCase()}_web_${regionCode}`,
          });
        });
    } else {
      setMessage("Please enter email address");
    }
  };

  const saveFormValues = async () => {
    setError({});
    setProcessing(true);

    const formErrors = validateForm();
    if (!_.isEmpty(formErrors)) {
      setError(formErrors);
      setProcessing(false);
      return;
    }

    if (recaptchaRef && recaptchaRef.current) {
      let captcha = "";
      recaptchaRef.current.reset();
      captcha = await recaptchaRef.current.executeAsync();
      const payload = {
        mat_email: email,
        mat_phone: mobileNumber,
        mat_is_existing_customer: isExistingCustomer,
        mat_email_otp_verified: "Yes",
        page_code: pageCode,
        page_name: pageName,
        page_url: pageUrl,
        form_code: formCode,
        invisible_recaptcha: true,
        captcha,
        utm_campaign,
        utm_medium,
        utm_source,
      };
      if (captcha) {
        saveForm(
          payload,
          (response) => {
            setProcessing(false);
            setFormSubmitted(true);
          },
          (error) => {
            setProcessing(false);
            setFormError(true);
            if (
              error.response &&
              error.response.status == 400 &&
              error.response.data.invalid_email
            ) {
              setError((prev) => ({
                ...prev,
                email: error.response.data.invalid_email,
              }));
            }
          }
        );
      } else {
        setProcessing(false);
      }
    } else {
      setProcessing(false);
    }
  };

  if (formSubmitted) {
    return <SuccessPage />;
  }

  if (formError) {
    return <ErrorPage onRetry={() => setFormError(false)} />;
  }

  if (showOtpForm) {
    return (
      <OTPForm
        pageName={pageName}
        regionCode={regionCode}
        requestOtp={requestOtp}
        email={email}
        setShowOtpForm={setShowOtpForm}
        saveFormValues={saveFormValues}
        pageNameGA={pageNameGA}
      />
    );
  }

  return (
    <Flex sx={styles.container} as="form" onSubmit={requestOtp}>
      <Box sx={styles.formContainer}>
        <Input
          type="tel"
          label="Phone Number"
          labelColor={colors.black}
          errorColor={colors.errorMain}
          value={mobileNumber}
          name="mobileNumber"
          maxlength={phoneNoLength}
          error={error?.mobile}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            setMobileNumber(value);
          }}
        />
        <Input
          label="Email Address"
          labelColor={colors.black}
          errorColor={colors.errorMain}
          name="email"
          value={email}
          maxlength={100}
          error={error?.email}
          onChange={(e) => {
            const newEmail = e.target.value;
            setEmail(newEmail);
            const emailErr = validate(newEmail);
            if (!emailErr) {
              setError((prev) => ({ ...prev, email: undefined }));
            }
          }}
        />
        <Box
          sx={{
            "@media screen and (min-width: 280px) and (max-width: 350px)": {
              px: "1.5rem",
            },
          }}
        >
          <Label>Are you a greytHR Customer?</Label>
          <Flex>
            <Label>
              <StyledRadio
                value="Yes"
                checked={isExistingCustomer === "Yes"}
                onChange={(e) => setIsExistingCustomer(e.target.value)}
                sx={
                  isExistingCustomer === "Yes"
                    ? style.selectedRadio
                    : style.unselectedRadio
                }
              />
              Yes
            </Label>
            <Label>
              <StyledRadio
                value="No"
                checked={isExistingCustomer === "No"}
                onChange={(e) => setIsExistingCustomer(e.target.value)}
                sx={
                  isExistingCustomer === "No"
                    ? style.selectedRadio
                    : style.unselectedRadio
                }
              />
              No
            </Label>
          </Flex>
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {processing ? (
          <Spinner color="white" size={58} />
        ) : (
          <Button
            variant="filled"
            size="large"
            disabled={!email || !mobileNumber}
          >
            Confirm
          </Button>
        )}
      </Box>
    </Flex>
  );
};

const UserInfoForm = ({
  getStartedSection,
  formCode,
  pageCode,
  pageName,
  pageUrl,
  regionCode,
  search,
  pageNameGA,
}) => {
  const { title, description, image } = getStartedSection;
  const featureImage = getImage(image);

  const searchParams = queryString.parse(search);

  return (
    <Box id="cta-block" sx={{ px: ["1rem", "1rem", "2rem", "4rem"] }}>
      <Heading as="h2" sx={styles.headingContainer}>
        <Text dangerouslySetInnerHTML={{ __html: title }} />
      </Heading>
      <Text as="p" sx={styles.textContainer}>
        <span dangerouslySetInnerHTML={{ __html: description }} />
      </Text>
      <Flex sx={{ justifyContent: "center", alignItems: "center" }}>
        <Box sx={{ display: ["none", "none", "none", "block"], mr: "3rem" }}>
          {featureImage && (
            <GatsbyImage
              image={featureImage}
              alt={image.alt}
              sx={{ width: "100%", height: "auto" }}
            />
          )}
        </Box>
        <Form
          formCode={formCode}
          pageUrl={pageUrl}
          pageCode={pageCode}
          pageName={pageName}
          regionCode={regionCode}
          searchParams={searchParams}
          pageNameGA={pageNameGA}
        />
      </Flex>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
        badge="bottomleft"
      />
    </Box>
  );
};

const styles = {
  container: {
    maxWidth: ["20.313rem", "20.313rem", "24.938rem", "24.938rem"],
    height: ["25.75rem", "25.75rem", "28.75rem", "28.75rem"],
    borderRadius: "20px",
    p: ["1.5rem", "1.5rem", "3rem", "3rem"],
    border: `1px solid ${colors.neutral200}`,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    columnGap: "1.5rem",
    rowGap: "1rem",
    mb: "3rem",
  },
  headingContainer: {
    ...typography.h2,
    p: { m: 0 },
    textAlign: "center",
    mb: "1.5rem",
  },
  textContainer: {
    ...typography.textLg500,
    textAlign: "center",
    mb: ["1.5rem", "1.5rem", "1.5rem", "3rem"],
  },
  otpContainer: {
    columnGap: "1.5rem",
    rowGap: "1rem",
    mb: "4.875rem",
  },
  otpHeading: {
    ...typography.h5,
    p: { m: 0 },
    mb: "0.75rem",
  },
};

const StyledRadio = styled(Radio)`
  color: ${colors.white};
  background-color: ${colors.white} !important;
  outline: none;
  box-shadow:
    0px 3px 6px -3px rgba(0, 0, 0, 0.05),
    0px 2px 4px -2px rgba(0, 0, 0, 0.05),
    0px 1px 2px -1px rgba(0, 0, 0, 0.05),
    0px 1px 0px -1px rgba(0, 0, 0, 0.05);
`;

const style = {
  unselectedRadio: {
    border: `1px solid ${colors.neutral300}`,
  },
  selectedRadio: {
    border: `8px solid ${colors.secondaryOrangeMain}`,
  },
};

UserInfoForm.propTypes = {
  pageCode: PropType.string,
  pageName: PropType.string,
  pageUrl: PropType.string,
  regionCode: PropType.string,
  search: PropType.any,
};

Form.propTypes = {
  pageCode: PropType.string,
  pageName: PropType.string,
  pageUrl: PropType.string,
  regionCode: PropType.string,
  searchParams: PropType.object,
};

OTPForm.propTypes = {
  pageCode: PropType.string,
  pageName: PropType.string,
  pageUrl: PropType.string,
  regionCode: PropType.string,
  searchParams: PropType.object,
};

Input.propTypes = {
  label: PropType.string,
  name: PropType.string,
  value: PropType.string,
  maxlength: PropType.number,
  error: PropType.string,
  onChange: PropType.func,
};

export default UserInfoForm;
